import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { get } from 'lodash-es'
import { chatbotEvents } from '../state'
import { portfolioApiEvents } from 'modules/api/portfolio'
import { paymentRuleClearanceTypes } from 'modules/api/portfolio/constants'

export interface LeaseInvoicesState {
  removedInvoiceIds: string[]
  updatedInvoiceIds: string[]
  invoices: any[]
}

export const initialState: LeaseInvoicesState = {
  removedInvoiceIds: [],
  updatedInvoiceIds: [],
  invoices: [],
}

const transformInvoicesFromSave = (invoices: any[]): any[] => {
  return invoices.map((i: any) => ({
    category: i.category,
    id: i.id,
    autoDeliver: i.autoDeliver,
    interval: i.interval,
    invoicePartyId: i.invoicePartyId,
    invoicePartyAccountType: i.invoicePartyAccountType,
    netAmount: i.netAmount,
    vatable: i.vatable,
    paymentRules: i.paymentRules.map((p: any) => {
      let clearance: any = {}
      if (parseFloat(p.beneficiary.value.amount) > 0) {
        clearance = {
          type: paymentRuleClearanceTypes.fixed,
          value: { grossAmount: p.beneficiary.value.amount, vat: p.beneficiary.value.vat },
        }
      } else {
        clearance = {
          type: paymentRuleClearanceTypes.notset,
          value: { notSet: true },
        }
      }
      return {
        ...p,
        clearance,
      }
    }),
    description: i.description,
  }))
}

const leaseInvoicesSlice = createSlice({
  name: 'chatbot/dialogs/leaseInvoices',
  initialState,
  reducers: {
    leaseInvoiceTemplatesSaved: (state, action: PayloadAction<{ invoices: any[] }>) => {
      state.invoices = transformInvoicesFromSave(action.payload.invoices)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(chatbotEvents.setCurrentPortfolio, (state, action) => {
        state.invoices = get(action.payload, 'invoiceTemplates', [])
      })
      .addCase(chatbotEvents.resetCurrentPortfolio, state => {
        // Create new arrays to avoid frozen object references from initialState
        state.removedInvoiceIds = []
        state.updatedInvoiceIds = []
        state.invoices = []
      })
      .addCase(portfolioApiEvents.invoiceTemplateRemoved, (state, action) => {
        const invoiceId = action.payload as string
        if (!state.removedInvoiceIds.includes(invoiceId)) {
          state.removedInvoiceIds.push(invoiceId)
        }
      })
      .addCase(portfolioApiEvents.invoiceTemplateChanged, (state, action) => {
        const invoiceId = action.payload as string
        if (!state.updatedInvoiceIds.includes(invoiceId)) {
          state.updatedInvoiceIds.push(invoiceId)
        }
      })
      .addCase(portfolioApiEvents.updateInvoiceTemplate_success, (state, action) => {
        state.updatedInvoiceIds = []
        state.invoices = action.payload.invoiceTemplates ?? []
      })
      .addCase(portfolioApiEvents.amendCommission_success, (state, action) => {
        state.invoices = action.payload.invoiceTemplates ?? []
      })
      .addCase(portfolioApiEvents.removeInvoiceTemplate_success, (state, action) => {
        state.removedInvoiceIds = []
        state.invoices = action.payload.invoiceTemplates ?? []
      })
      .addCase(portfolioApiEvents.addInvoiceTemplate_success, (state, action) => {
        state.invoices = action.payload.invoiceTemplates ?? []
      })
      .addCase(portfolioApiEvents.updateTenantParties_success, (state, action) => {
        const index = state.invoices.findIndex((inv: any) => inv.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
      })
      .addCase(portfolioApiEvents.updateOwnerParties_success, (state, action) => {
        state.invoices = action.payload.invoiceTemplates ?? []
      })
      .addCase(portfolioApiEvents.amendRentAndFees_success, (state, action) => {
        state.invoices = action.payload.invoiceTemplates ?? []
      })
  },
})

export const { reducer } = leaseInvoicesSlice
export const leaseInvoicesEvents = leaseInvoicesSlice.actions
